import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useId, useMemo } from 'react';
import { BusyIndeterminateInline } from '@/components/misc/busy';
import { Chart } from '@/components/misc/chart';
import { formatPrice } from '@/components/misc/format-price';
import { salesPerformanceChartOptions, type SalesPerformanceChartData } from './salesPerformanceCharts';

const percentageFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export type OverviewGenericCardProps = {
  title: string;
  value: string | number;
  loading: boolean;
  data?: SalesPerformanceChartData;
  isPrice?: boolean;
};

export const OverviewGenericCard = ({ title, value, loading, data, isPrice = false }: OverviewGenericCardProps) => {
  const theme = useTheme();
  const chartId = useId();
  const titleId = useId();
  const chartOptions = useMemo(() => salesPerformanceChartOptions(theme, chartId, data), [chartId, data, theme]);
  const percentage = data?.pct;
  const visiblePercentage = typeof percentage === 'number' && Number.isFinite(percentage) && percentage !== 0 ? percentage : null;
  const hasSeries = Boolean(data?.series.length);

  return (
    <Card component='section' aria-labelledby={titleId} sx={{ mb: 1 }}>
      <CardContent sx={{ p: 2, backgroundColor: 'background.paper', '&:last-child': { pb: 2 } }}>
        <Typography id={titleId} color='textSecondary' variant='overline'>
          {title}
        </Typography>

        <Box sx={{ display: 'flex', gap: 4, justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant='h4'>
              {loading && <BusyIndeterminateInline height='1.375em' justify='center' color='primary' />}
              {!loading && isPrice && formatPrice(Math.floor(Number(value)), true)}
              {!loading && !isPrice && Math.floor(Number(value)).toLocaleString('en-US')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant='h6'>
              <Box
                sx={{
                  color: visiblePercentage !== null && visiblePercentage < 0 ? 'error.dark' : 'success.dark',
                  display: 'flex',
                  fontWeight: 'bold',
                  pt: 1.625,
                }}
              >
                {!loading && visiblePercentage !== null && `${percentageFormatter.format(visiblePercentage)}%`}
              </Box>
            </Typography>
          </Box>
        </Box>

        {hasSeries && <Divider />}
        {hasSeries && data && (
          <Box sx={{ alignItems: 'flex-start', pt: 1 }}>
            <Chart height={200} options={chartOptions} series={data.series} type='bar' />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
